import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { DEFAULT_RELEASE_THEME, RELEASE_THEME_IDS, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { themeMessages } from './themeMessages';
import { ThemeSample } from './ThemeSample';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeTileProps {
  themeId: ReleaseThemeId;
  isOn: boolean;
  playing: boolean;
  tabIndex: number;
  buttonRef: (button: HTMLButtonElement | null) => void;
  onPick: (id: ReleaseThemeId) => void;
  onActivate: (id: ReleaseThemeId | null) => void;
  /** The arrow keys of the radio group: each radio moves the focus. */
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

const ThemeTileComponent: React.FC<ThemeTileProps> = ({
  themeId,
  isOn,
  playing,
  tabIndex,
  buttonRef,
  onPick,
  onActivate,
  onKeyDown,
}) => {
  const intl = useIntl();
  const pick = useCallback(() => onPick(themeId), [onPick, themeId]);
  const activate = useCallback(() => onActivate(themeId), [onActivate, themeId]);
  const deactivate = useCallback(() => onActivate(null), [onActivate]);
  const messages = releaseThemeMessages[themeId];
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={isOn}
      tabIndex={tabIndex}
      className={clsx(styles.tile, isOn && styles.tileOn)}
      data-testid={`theme-tile-${themeId}`}
      onClick={pick}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      onFocus={activate}
      onBlur={deactivate}
      onKeyDown={onKeyDown}
    >
      <ThemeSample themeId={themeId} playing={playing} />
      <b className={styles.tileName}>
        {intl.formatMessage(messages.name)}
        {themeId === DEFAULT_RELEASE_THEME && (
          <span className={styles.badge}>{intl.formatMessage(themeMessages.defaultBadge)}</span>
        )}
        {isOn && (
          <span className={styles.tick} aria-hidden="true">
            ✓
          </span>
        )}
      </b>
      <small className={styles.tileLine}>{intl.formatMessage(messages.line)}</small>
    </button>
  );
};

const ThemeTile = TypedMemo(ThemeTileComponent);

interface ThemeTilesProps {
  value: ReleaseThemeId;
  onChange: (id: ReleaseThemeId) => void;
}

/**
 * The 8 theme tiles (design q2, q2b, q4). Each tile plays the rendered sample loop of its theme.
 * Only one tile plays at a time: the hovered or focused tile, else the selected tile. Touch has no
 * hover, so a tap selects and plays. A radio group: the arrow keys move the focus.
 */
const ThemeTilesComponent: React.FC<ThemeTilesProps> = ({ value, onChange }) => {
  const intl = useIntl();
  const [activeId, setActiveId] = useState<ReleaseThemeId | null>(null);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const playingId = activeId ?? value;

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const current = buttons.current.findIndex((button) => button === document.activeElement);
    const next = (Math.max(0, current) + step + RELEASE_THEME_IDS.length) % RELEASE_THEME_IDS.length;
    buttons.current[next]?.focus();
  }, []);

  return (
    <div className={styles.tiles} role="radiogroup" aria-label={intl.formatMessage(themeMessages.tiles)}>
      {RELEASE_THEME_IDS.map((themeId, index) => (
        <ThemeTile
          key={themeId}
          themeId={themeId}
          isOn={themeId === value}
          playing={themeId === playingId}
          tabIndex={themeId === value ? 0 : -1}
          buttonRef={(button) => {
            buttons.current[index] = button;
          }}
          onPick={onChange}
          onActivate={setActiveId}
          onKeyDown={onKeyDown}
        />
      ))}
    </div>
  );
};

export const ThemeTiles = TypedMemo(ThemeTilesComponent);
